import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { expandEndpoint } from "@visioni/a3s-client-api";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { refreshDelayMs, REFRESH_MIN_INTERVAL_MS } from "./refresh-throttle";
import {
  notePress,
  reportFor,
  watchForSubject,
  watchProgress,
  WATCH_POLL_INTERVAL_MS,
  type ArmedWatch,
  type SignalPathCounts,
  type WatchReport,
} from "./signal-watch";

export type { WatchOutcome, WatchReport } from "./signal-watch";
import type { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import { triggerTestSignalMutation } from "./commands";
import { listTestSignalLogEndpoint } from "../client-messages/test-signals";
import type {
  ListTestSignalLogResponse,
  TestSignalLogItem,
  TestSignalPath,
  TriggerTestSignalResponse,
} from "../client-messages/test-signals";

export interface TriggerTestSignalVariables {
  siteId: string;
  cameraKey: string;
  signalPath: TestSignalPath;
}

/**
 * Fires an on-demand test signal at one camera. Support-only — the API answers
 * 403 for anyone else, and for a camera outside the rollout flag.
 *
 * A resolved mutation means IoT Core accepted the publish, not that the camera
 * produced a signal. Callers must not report success as "a test signal fired".
 */
export function useTriggerTestSignal() {
  const apiConfig = useApiConfig();

  const mutationConfig =
    apiConfig.tenant ?
      {
        tenantId: apiConfig.tenant.tenantId,
        apiBaseUrl: apiConfig.tenant.apiBaseUrl,
        authenticatedFetch: apiConfig.authenticatedFetch,
      }
    : { authenticatedFetch: apiConfig.authenticatedFetch };

  return useMutation<TriggerTestSignalResponse, Error, TriggerTestSignalVariables>({
    mutationFn: ({ siteId, cameraKey, signalPath }) => {
      const { mutationFn } = triggerTestSignalMutation(mutationConfig, {
        siteId: siteId as SiteId,
        assetCameraKey: cameraKey as AssetCameraKey,
      });
      return mutationFn({ signalPath });
    },
  });
}

/**
 * Rate-limits manual refreshes to one request per {@link REFRESH_MIN_INTERVAL_MS}.
 *
 * Deliberately invisible. A press inside the window is not rejected and the
 * button is not disabled with a countdown — the refresh is simply scheduled for
 * the moment the window closes, and `isPending` keeps the button in its normal
 * loading state until then. To the operator a hurried second press looks like a
 * slightly slow refresh rather than a control fighting them.
 *
 * Presses inside the window collapse into one send.
 *
 * Exists to keep a frustrated operator from hammering the Lambda, not to save
 * money — a page of the log is a fraction of a cent.
 */
function useThrottledRefresh(
  refetch: () => Promise<unknown>,
  /**
   * Identity of what is being refreshed. Changing it discards the throttle:
   * the state below is per-instance, and this component is reused across
   * cameras when only the route params change.
   */
  subject: string,
): {
  readonly refresh: () => void;
  readonly isPending: boolean;
} {
  const lastSentAt = useRef<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isPending, setIsPending] = useState(false);

  /**
   * Discard the throttle on unmount, and whenever the camera changes.
   *
   * Both matter. On unmount a pending timer would fire against a page that is
   * gone. On a camera change the component is reused, so the previous camera's
   * throttle would otherwise carry over — delaying the new camera's *first*
   * press by up to the full interval and showing its Refresh button as already
   * in progress.
   */
  useEffect(
    () => () => {
      if (timer.current !== null) {
        clearTimeout(timer.current);
        timer.current = null;
      }
      lastSentAt.current = null;
      setIsPending(false);
    },
    [subject],
  );

  const send = useCallback(() => {
    lastSentAt.current = Date.now();
    // Fire and forget: the query's own state drives the UI, and a rejection is
    // surfaced through `isError` rather than here.
    void refetch();
  }, [refetch]);

  const refresh = useCallback(() => {
    if (timer.current !== null) return;

    const delay = refreshDelayMs(lastSentAt.current, Date.now(), REFRESH_MIN_INTERVAL_MS);
    if (delay === 0) {
      send();
      return;
    }

    setIsPending(true);
    timer.current = setTimeout(() => {
      timer.current = null;
      setIsPending(false);
      send();
    }, delay);
  }, [send]);

  return { refresh, isPending };
}

/**
 * Page size for a camera's test signal log.
 *
 * Bounded by how tall a row renders rather than by how much history fits: each
 * carries a header line, a cloud line, a delivery line and often a detail line,
 * so a larger page is a page nobody scrolls to the end of.
 *
 * The endpoint caps at 100. The list is a plain map rather than a virtualised
 * one, so paging accumulates rows in the tree — another reason to keep the
 * window small.
 */
const LOG_PAGE_LIMIT = 20;

export interface UseCameraTestSignalLogResult {
  readonly entries: readonly TestSignalLogItem[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
  readonly hasNextPage: boolean;
  readonly isFetchingNextPage: boolean;
  readonly fetchNextPage: () => void;
  /** True while any fetch for this log is in flight, including a manual refresh. */
  readonly isFetching: boolean;
  /** Outstanding legs and when the window closes, or `null` when nothing is being watched for. */
  readonly watching: { readonly remaining: SignalPathCounts; readonly deadline: number } | null;
  /** Closed windows and how each turned out, newest first. */
  readonly watchReports: readonly WatchReport[];
  /** Reads the log now, bypassing the refresh throttle. `null` when it could not be read. */
  readonly readLog: () => Promise<readonly TestSignalLogItem[] | null>;
  /**
   * Call after a trigger succeeds, with the log as {@link readLog} found it
   * just before the command was sent: starts or extends the watch for that leg.
   */
  readonly noteTriggered: (signalPath: TestSignalPath, readAtPress: readonly TestSignalLogItem[]) => void;
  readonly dismissWatchReport: (id: string) => void;
  /** Re-reads the log from the first page. Backs the page's Refresh control. */
  readonly refresh: () => void;
}

export interface UseCameraTestSignalLogOptions {
  siteId: string;
  cameraKey: string;
  /** Gate the query — e.g. behind the page's support-user check. */
  enabled?: boolean;
}

/**
 * Lists one camera's test signal log, newest-first: the commands an operator
 * sent and what the pipeline then did with the signals they produced.
 *
 * Cursor-based paging — the endpoint forwards DynamoDB's `LastEvaluatedKey` as
 * an opaque `cursor` and `useInfiniteQuery` threads it through, mirroring
 * `useCameraConfirmedDumps`. Rows carry raw ISO timestamps and the stored
 * outcome slug; the consumer formats the date and resolves the human phrase, so
 * the endpoint stays a pure data projection with no server-side timezone
 * dependency.
 */
export function useCameraTestSignalLog(options: UseCameraTestSignalLogOptions): UseCameraTestSignalLogResult {
  const apiConfig = useApiConfig();
  const tenant = apiConfig.tenant;
  const enabled = (options.enabled ?? true) && !!tenant;
  const subject = `${options.siteId}#${options.cameraKey}`;

  const [armedWatch, setArmedWatch] = useState<ArmedWatch | null>(null);
  const [watchReports, setWatchReports] = useState<readonly WatchReport[]>([]);
  /**
   * The clock the watch is judged against. Moved only at the two instants that
   * can change the answer — a press, and the deadline passing. Everything else
   * about a watch turns on `entries`, not on time, so a ticking clock here
   * would re-render the page once a second to no effect. The countdown gets
   * `deadline` and ticks itself.
   */
  const [now, setNow] = useState(() => Date.now());
  const nextReportId = useRef(0);
  /** The camera the hook is currently rendered against, for late callbacks to check. */
  const currentSubject = useRef(subject);
  useEffect(() => {
    currentSubject.current = subject;
  }, [subject]);

  /**
   * The watch, but only if it was armed for the camera on screen.
   *
   * A route change re-renders this hook with a new `subject` while the armed
   * watch is still the previous camera's, and every consumer below — the poll,
   * the deadline timer, the progress, the countdown, the report — would
   * otherwise be about a signal that was never sent here. `null` makes all of
   * them stand down until this camera arms its own watch.
   */
  const watch = watchForSubject(armedWatch, subject);

  const query = useInfiniteQuery<
    ListTestSignalLogResponse,
    Error,
    InfiniteData<ListTestSignalLogResponse, string | undefined>,
    readonly unknown[],
    string | undefined
  >({
    queryKey: logQueryKey(tenant?.tenantId, options.siteId, options.cameraKey),
    enabled,
    // The app caches queries for five minutes globally and does not refetch on
    // window focus. Both are wrong for this log: it is the page's answer to
    // "did my signal get through?", and a signal lands a minute or two after
    // the command that produced it. Served from cache, the page would show the
    // command and never the signal. Always refetch on mount, and treat what is
    // held as immediately stale so a remount genuinely re-reads.
    staleTime: 0,
    refetchOnMount: "always",
    // The query client will not start a poll while one is in flight, so a slow
    // or timing-out endpoint delays the next poll instead of stacking requests
    // behind it.
    //
    // A poll re-reads every loaded page, not just the first — React Query v5
    // dropped per-page refetching. Only the first page can carry an arrival, so
    // the rest is waste, but bounded: someone who paged back and then fired is
    // rare, and a page of the log is a fraction of a cent.
    refetchInterval: watch === null ? false : WATCH_POLL_INTERVAL_MS,
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.cursor,
    queryFn: async ({ pageParam, signal }) => {
      // `enabled` gates the call; this defensively narrows the optional tenant.
      if (!tenant) throw new Error("Tenant not ready");
      const url =
        tenant.apiBaseUrl +
        expandEndpoint(listTestSignalLogEndpoint, {
          path: {
            tenantId: tenant.tenantId,
            siteId: options.siteId as SiteId,
            assetCameraKey: options.cameraKey as AssetCameraKey,
          },
          query: { limit: LOG_PAGE_LIMIT, ...(pageParam !== undefined && { cursor: pageParam }) },
        });

      const response = await apiConfig.authenticatedFetch(url, { signal });
      if (!response.ok) throw new Error(await readErrorMessage(response));
      const data: unknown = await response.json();
      return listTestSignalLogEndpoint.responseSchema.parse(data);
    },
  });

  const throttled = useThrottledRefresh(query.refetch, subject);

  const entries = useMemo<readonly TestSignalLogItem[]>(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data],
  );

  const progress = watch === null ? null : watchProgress(watch, entries, now);

  /**
   * Wake once, when the window closes, so the render below sees a clock past
   * the deadline and the effect further down can file the failure.
   *
   * A timeout rather than a poll: a deadline is a single known instant, and
   * asking the clock every second whether it has arrived yet costs a full page
   * render each time. Rescheduled whenever `watch` changes, so a press that
   * extends the window moves the wake-up with it.
   *
   * The timer, not the wall clock, is the authority on elapsed time: a timer can
   * fire a shade early, and a wall clock can step backwards under NTP, either of
   * which would leave the deadline still in the future. Since nothing else
   * re-runs this effect, the watch would then poll on forever without ever
   * closing, so the snapshot is clamped to the deadline. A late wake-up is
   * unaffected — the clock is already past the deadline and the clamp is a
   * no-op.
   */
  useEffect(() => {
    if (watch === null) return;
    const timer = setTimeout(
      () => setNow(Math.max(Date.now(), watch.deadline)),
      Math.max(0, watch.deadline - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [watch]);

  /**
   * File a report for every watch that closes, however it closed, and clear it.
   *
   * Reports accumulate. Two windows are two presses, each with its own verdict
   * and its own dismiss, because rolling them together loses which press the
   * verdict belongs to.
   *
   * Safe to append to this camera's reports because `watch` is only ever the
   * watch armed for this camera: a watch is never scored against another
   * camera's log, so a verdict here is always about a signal sent here.
   */
  useEffect(() => {
    if (progress === null || progress.kind === "watching" || watch === null) return;

    const id = `watch-${String(nextReportId.current++)}`;
    // A pass closes the moment the last signal lands; everything else closes at
    // the deadline, which is the instant the report is actually about.
    const closedAt = progress.kind === "satisfied" ? Date.now() : watch.deadline;
    const missing = progress.kind === "satisfied" ? {} : progress.remaining;
    setWatchReports((previous) => [{ id, ...reportFor(watch, missing, closedAt) }, ...previous]);
    setArmedWatch(null);
  }, [progress, watch]);

  /**
   * Drop the watch and the reports on unmount, and whenever the camera changes —
   * this component is reused across cameras when only the route params change.
   * A watch is per-sitting, exactly like the reports beside it: both name
   * signals expected at the camera they were raised for.
   *
   * The camera tag on the watch and this clearing guard different failures, and
   * neither makes the other redundant. The tag covers the render straight after
   * a route change, when this cleanup has run but no effect body has — React
   * flushes every cleanup before it runs any effect — so a watch still held in
   * state is not scored against the camera just arrived at. The clearing covers
   * the return trip: without it, coming back to a camera resurrects a watch
   * whose window elapsed while the operator was elsewhere, and the deadline
   * timer closes it at once against a cached log, filing a failure for a signal
   * that may well have arrived.
   */
  useEffect(
    () => () => {
      setArmedWatch(null);
      setWatchReports([]);
    },
    [subject],
  );

  /**
   * Reads the log now, outside the manual-refresh throttle, and answers with
   * what it holds — or `null` if it could not be read.
   *
   * Exists so a press can take its watermark from the log as it stands rather
   * than from whatever the page happens to be showing, which may be a cache
   * written up to thirty days ago. A `null` answer is a refusal to fire, not a
   * degraded mode — the caller must not send a command it cannot report on.
   */
  const readLog = useCallback(async (): Promise<readonly TestSignalLogItem[] | null> => {
    const result = await query.refetch();
    if (result.data === undefined) return null;
    return result.data.pages.flatMap((page) => page.data);
  }, [query]);

  const noteTriggered = useCallback(
    (signalPath: TestSignalPath, readAtPress: readonly TestSignalLogItem[]) => {
      // A trigger whose request outlived the camera it was fired at: that
      // camera's watch has already been cleared, and arming one here would make
      // the camera now on screen poll for — and then report a failure for — a
      // signal that was sent somewhere else.
      if (currentSubject.current !== subject) return;
      const at = Date.now();
      setNow(at);
      // Extend only this camera's own window: a press here after a camera
      // change starts a fresh one rather than inheriting the previous camera's
      // watermark and expectations.
      setArmedWatch((previous) => ({
        subject,
        watch: notePress(watchForSubject(previous, subject), signalPath, readAtPress, at),
      }));
    },
    [subject],
  );

  const dismissWatchReport = useCallback((id: string) => {
    setWatchReports((previous) => previous.filter((report) => report.id !== id));
  }, []);

  return {
    entries,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error instanceof Error ? query.error : null,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => {
      void query.fetchNextPage();
    },
    // Covers the throttle's wait as well as the request itself, so a press
    // inside the window reads as a slow refresh rather than a dead button.
    isFetching: query.isFetching || throttled.isPending,
    // `deadline` rather than a time-remaining: the consumer redraws the
    // countdown on its own clock, so this value never goes stale between
    // renders and the hook does not have to re-render the page to keep it true.
    watching:
      progress?.kind === "watching" && watch !== null ?
        { remaining: progress.remaining, deadline: watch.deadline }
      : null,
    watchReports,
    readLog,
    noteTriggered,
    dismissWatchReport,
    refresh: throttled.refresh,
  };
}

function logQueryKey(tenantId: string | undefined, siteId: string, cameraKey: string): readonly unknown[] {
  return ["monitoring", "test-signal-log", tenantId, siteId, cameraKey, LOG_PAGE_LIMIT] as const;
}

/**
 * Refetches a camera's log now, for use after firing — the operator's own
 * command should appear at once rather than on the next natural refetch.
 *
 * A hook rather than an exported key because the key carries the tenant, which
 * only `useApiConfig` knows: a caller building the key itself would have to
 * either re-read the tenant or pass `undefined` and silently invalidate
 * nothing.
 */
export function useRefreshCameraTestSignalLog(): (siteId: string, cameraKey: string) => void {
  const queryClient = useQueryClient();
  const tenantId = useApiConfig().tenant?.tenantId;

  return (siteId, cameraKey) => {
    void queryClient.invalidateQueries({ queryKey: logQueryKey(tenantId, siteId, cameraKey) });
  };
}

/** Best-effort message from a failed response — JSON `message`, raw text, or the status. */
async function readErrorMessage(response: Response): Promise<string> {
  try {
    const text = await response.text();
    if (!text) return `Request failed: ${String(response.status)}`;
    try {
      const json = JSON.parse(text) as { message?: string };
      return json.message ?? text;
    } catch {
      return text;
    }
  } catch {
    return `Request failed: ${String(response.status)}`;
  }
}
